/**
 * The parked produce cart beside Ada's stall (lifted from lantern-water's
 * `market-cart.js`): axle and undercarriage, plank bed, two spoked wheels with
 * iron tyres, slatted sides, shafts resting on supports, two tied sacks and an
 * open crate of fruit. Authored at the local origin with its shafts toward +z;
 * the prototype stood it at (-4.7, 8.1) turned by -0.18 rad, which is the
 * placement's (x -4.7, y -8.1, rotation -10.313°). No params.
 */
import * as THREE from 'three';
import type { Ctx } from '../kit.ts';
import type { Prefab } from './types.ts';

export const prefab: Prefab = {
  id: 'market_cart',
  buildPrefab(ctx) {
    const m = ctx.m;
    const box = (...a: Parameters<Ctx['box']>): ReturnType<Ctx['box']> => ctx.box(...a);
    const mesh = (...a: Parameters<Ctx['mesh']>): ReturnType<Ctx['mesh']> => ctx.mesh(...a);
    const line = (...a: Parameters<Ctx['line']>): ReturnType<Ctx['line']> => ctx.line(...a);
    const p = new THREE.Group();
    p.name = 'market-delivery-cart';
    const wheelY = 0.57;
    const wheelZ = -0.12;
    line(p, [-1.04, wheelY, wheelZ], [1.04, wheelY, wheelZ], 0.065, m.iron);
    for (const x of [-0.48, 0.48]) box(p, x, 0.62, 0, 0.13, 0.15, 1.82, m.darkWood);
    for (let i = 0; i < 7; i += 1) box(p, -0.64 + i * 0.213, 0.75, 0, 0.201, 0.09, 1.73, m.wood);
    for (const z of [-0.75, 0.72]) box(p, 0, 0.655, z, 1.48, 0.11, 0.12, m.darkWood);
    for (const side of [-1, 1]) {
      const wheel = new THREE.Group();
      wheel.position.set(side * 0.91, wheelY, wheelZ);
      wheel.rotation.y = Math.PI / 2;
      p.add(wheel);
      mesh(wheel, new THREE.TorusGeometry(0.49, 0.058, 6, 32), m.wood, 0, 0, 0);
      mesh(wheel, new THREE.TorusGeometry(0.552, 0.018, 4, 32), m.iron, 0, 0, 0);
      const hub = mesh(wheel, new THREE.CylinderGeometry(0.105, 0.13, 0.24, 12), m.darkWood, 0, 0, 0);
      hub.rotation.x = Math.PI / 2;
      const pin = mesh(wheel, new THREE.CylinderGeometry(0.053, 0.053, 0.265, 8), m.iron, 0, 0, 0);
      pin.rotation.x = Math.PI / 2;
      for (let i = 0; i < 10; i += 1) {
        const a = (i * Math.PI) / 5 + 0.13;
        line(wheel, [Math.cos(a) * 0.105, Math.sin(a) * 0.105, 0], [Math.cos(a) * 0.475, Math.sin(a) * 0.475, side * 0.012], 0.026, m.wood);
      }
      mesh(wheel, new THREE.TorusGeometry(0.097, 0.017, 4, 12), m.iron, 0, 0, side * 0.108);
    }
    for (const side of [-1, 1]) {
      for (const z of [-0.78, 0.76]) box(p, side * 0.7, 1.02, z, 0.085, 0.62, 0.085, m.darkWood);
      for (let j = 0; j < 3; j += 1) box(p, side * 0.7, 0.86 + j * 0.18, 0, 0.065, 0.135, 1.7, m.wood);
      for (const z of [-0.77, 0.76]) for (let j = 0; j < 3; j += 1) mesh(p, new THREE.SphereGeometry(0.023, 5, 3), m.iron, side * 0.741, 0.86 + j * 0.18, z);
      line(p, [side * 0.51, 0.65, 0.3], [side * 0.6, 0.63, 2.1], 0.052, m.wood);
      line(p, [side * 0.6, 0.63, 1.78], [side * 0.6, 0.63, 2.16], 0.06, m.darkWood);
      line(p, [side * 0.54, 0.63, 1.19], [side * 0.56, 0.07, 1.34], 0.047, m.darkWood);
      box(p, side * 0.56, 0.045, 1.34, 0.18, 0.07, 0.21, m.iron);
    }
    for (let j = 0; j < 3; j += 1) box(p, 0, 0.86 + j * 0.18, -0.8, 1.42, 0.135, 0.065, m.wood);
    const sacks: readonly (readonly [number, number, number, number])[] = [
      [-0.32, -0.36, 1, -0.09],
      [0.29, -0.41, 0.86, 0.12],
    ];
    const outline: readonly (readonly [number, number])[] = [
      [0, 0],
      [0.2, 0.01],
      [0.285, 0.11],
      [0.29, 0.31],
      [0.245, 0.52],
      [0.12, 0.64],
      [0.09, 0.69],
      [0.14, 0.74],
      [0.115, 0.79],
      [0, 0.775],
    ];
    for (const [x, z, scale, lean] of sacks) {
      const sack = new THREE.Group();
      sack.position.set(x, 0.795, z);
      sack.rotation.z = lean;
      sack.scale.setScalar(scale);
      p.add(sack);
      const geo = new THREE.LatheGeometry(
        outline.map(([a, b]) => new THREE.Vector2(a, b)),
        18,
      );
      const pos = geo.getAttribute('position');
      for (let i = 0; i < pos.count; i += 1) {
        const a = Math.atan2(pos.getZ(i), pos.getX(i));
        const y = pos.getY(i);
        const fold = 1 + Math.sin(a * 7 + y * 6) * 0.025 + Math.sin(a * 11) * 0.015;
        pos.setXYZ(i, pos.getX(i) * fold, y, pos.getZ(i) * fold);
      }
      geo.computeVertexNormals();
      mesh(sack, geo, m.canvas, 0, 0, 0);
      const tie = mesh(sack, new THREE.TorusGeometry(0.102, 0.018, 4, 14), m.darkWood, 0, 0.685, 0);
      tie.rotation.x = Math.PI / 2;
      line(sack, [0.025, 0.68, 0.105], [0.07, 0.55, 0.19], 0.009, m.wood);
      line(sack, [0.025, 0.68, 0.105], [-0.035, 0.56, 0.19], 0.009, m.wood);
      for (let i = 0; i < 8; i += 1) line(sack, [0.18, 0.09 + i * 0.055, 0.16], [0.195, 0.11 + i * 0.055, 0.16], 0.006, m.wood);
    }
    const cargo = new THREE.Group();
    cargo.position.set(0, 0.8, 0.37);
    p.add(cargo);
    box(cargo, 0, 0.03, 0, 1.1, 0.055, 0.67, m.darkWood);
    for (const x of [-0.53, 0.53]) for (const z of [-0.3, 0.3]) box(cargo, x, 0.21, z, 0.055, 0.41, 0.055, m.wood);
    for (let j = 0; j < 3; j += 1) {
      for (const z of [-0.325, 0.325]) box(cargo, 0, 0.095 + j * 0.12, z, 1.1, 0.085, 0.04, m.wood);
      for (const x of [-0.55, 0.55]) box(cargo, x, 0.095 + j * 0.12, 0, 0.04, 0.085, 0.65, m.wood);
    }
    const fruit = ctx.kit.shared('market-cart:fruit', () => new THREE.IcosahedronGeometry(0.103, 1));
    for (let row = 0; row < 3; row += 1) {
      for (let col = 0; col < 5; col += 1) {
        const x = -0.42 + col * 0.21 + (row % 2) * 0.022;
        const z = -0.2 + row * 0.2;
        const y = 0.34 + ((row + col) % 3) * 0.025;
        mesh(cargo, fruit, (row + col) % 4 ? m.red : m.moss, x, y, z, 1, 0.9, 1);
        line(cargo, [x, y + 0.072, z], [x + 0.01, y + 0.12, z], 0.009, m.darkWood);
      }
    }
    const root = new THREE.Group();
    root.add(p);
    return { root };
  },
};
